import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import type { RatePlanFindInterface } from '@zaoblako/shared/modules/saas/dtos/rate-plan/rate-plan-find.dto';
import {
  RatePlanFormDto,
  type RatePlanFormInterface
} from '@zaoblako/shared/modules/saas/dtos/rate-plan/rate-plan-form.dto';
import { ApplicationTabRatePlanFindInterface } from '@zaoblako/shared/modules/security/dtos/application/tab/application-tab-rate-plan-find.dto';
import { BasisRatePlanService } from '@zaoblako/web/core/services/basis/basis-rate-plan.service';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';
import { EditorComponent } from '@zaoblako/web/shared/dumbs/editor/editor.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import {
  APPLICATION_MODULES,
  APPLICATION_ACCESS_CONTROL_RATE_PLAN_REFERENCE
} from '@zaoblako/shared/modules/security/constants/application-access-control.constants';
import { TranslateModulesPipe } from '@zaoblako/web/shared/pipes/translate-modules.pipe';
import { TranslateModuleDescriptionPipe } from '@zaoblako/web/shared/pipes/translate-module-description.pipe';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { Observable } from 'rxjs';

@Component({
  selector: 'crm-rate-plan-form',
  standalone: true,
  imports: [
    CommonModule,
    SpinnerComponent,
    CardModule,
    InputTextComponent,
    JoinPipe,
    EditorComponent,
    InputNumberModule,
    FormsModule,
    ButtonComponent,
    MultiSelectComponent,
    CheckboxModule,
    TranslateModulesPipe,
    TranslateModuleDescriptionPipe
  ],
  templateUrl: './rate-plan-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RatePlanFormComponent extends BaseFormComponent<RatePlanFormInterface> implements OnInit {
  @Input() public readonly index: number;
  public form = <RatePlanFormInterface>{
    modulesAccessControl: JSON.parse(JSON.stringify(APPLICATION_ACCESS_CONTROL_RATE_PLAN_REFERENCE))
  };

  public dto = RatePlanFormDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public modules = Object.keys(APPLICATION_MODULES).filter((key) => key !== 'BASIS');
  public compatAvailableModules: string[] = [];

  public editableTabs: string[] = [];
  public excludedTabs: string[] = [];

  private tabMetadataCache = new Map<string, { tabName: string; moduleName: string }>();

  public constructor(
    private readonly basisRatePlanService: BasisRatePlanService,
    private readonly formStoreService: FormStoreService,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService,
    private readonly router: Router,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit() {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    this.form.isDefault = false;
    this.initMetadataCache();

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getTabDisplayName = (key: string) => this.tabMetadataCache.get(key)?.tabName || key;
  public getModuleNameByTab = (key: string) => this.tabMetadataCache.get(key)?.moduleName || '';
  public getModuleDisplayName = (key: string) => APPLICATION_MODULES[key]?.displayName || key;
  public getModuleDescription = (key: string) => APPLICATION_MODULES[key]?.description || '';

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.basisRatePlanService
      .findById<RatePlanFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (item) => {
          this.form = item;
          this.formStoreService.setLoading(false, this.index);
          this.syncInternalState();
        }
      });
  }

  public saveItem() {
    this.form.modulesAccessControl = JSON.parse(JSON.stringify(APPLICATION_ACCESS_CONTROL_RATE_PLAN_REFERENCE));

    for (const [modKey, module] of Object.entries(this.form.modulesAccessControl)) {
      module.enabled = this.compatAvailableModules.includes(modKey) || modKey === 'BASIS';

      for (const section of Object.values(module.sections)) {
        for (const [tabKey, tab] of Object.entries(section.tabs)) {
          tab.enabled = !(this.excludedTabs ?? []).includes(tabKey);
        }
      }
    }

    if (this.checkValidate(this.form).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.basisRatePlanService.update<RatePlanFormInterface, RatePlanFindInterface>(this.form).subscribe({
          next: (ratePlan) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`Тариф "${this.form?.name}" обновлен`);
            this.formStoreService.saveNext(ratePlan, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.basisRatePlanService.create<RatePlanFindInterface>(this.form).subscribe({
          next: (task) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Тариф создан');
            if (this.formStoreService.getState.forms[this.index]?.isRedirectInCreate) {
              this.formStoreService.closeSidebar(this.index);
              this.router.navigate(['/admin/rate-plan/card/' + task.id]).catch(console.error);
            } else {
              this.formStoreService.saveNext(task, this.index);
            }
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteRatePlan() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить тариф "${this.form?.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.basisRatePlanService
          .deleteById(this.formStoreService.getState.forms[this.index]?.entityId)
          .subscribe(() => {
            this.errorService.addSuccessMessage(`Тариф "${this.form?.name}" удален`);
            this.formStoreService.deleteNext(this.index);
          });
      }
    });
  }

  public onModulesChange() {
    if (!this.compatAvailableModules?.length) return;

    const hasFinance = this.compatAvailableModules.includes('FINANCE');
    const dependents = ['BRS', 'BUSINESS'];

    if (!hasFinance) {
      this.compatAvailableModules = this.compatAvailableModules.filter((m) => !dependents.includes(m));
    }

    if (!this.compatAvailableModules.includes('BASIS')) {
      this.compatAvailableModules.push('BASIS');
    }

    this.updateEditableTabsList();
  }

  private syncInternalState() {
    this.compatAvailableModules = Object.entries(this.form.modulesAccessControl)
      .filter(([key, mod]) => mod.enabled && key !== 'BASIS')
      .map(([key]) => key);

    const excluded: string[] = [];
    this.traverseTabs(this.form.modulesAccessControl, (modKey, secKey, tabKey, tab) => {
      if (!tab.enabled && tab.editable) excluded.push(tabKey);
    });
    this.excludedTabs = excluded;
    this.updateEditableTabsList();
  }

  private updateEditableTabsList() {
    const available: string[] = [];
    this.traverseTabs(APPLICATION_ACCESS_CONTROL_RATE_PLAN_REFERENCE, (modKey, _, tabKey, tab) => {
      if (tab.editable && this.compatAvailableModules.includes(modKey)) {
        available.push(tabKey);
      }
    });

    this.editableTabs = available.sort((a, b) => {
      const nameA = this.tabMetadataCache.get(a)?.moduleName || '';
      const nameB = this.tabMetadataCache.get(b)?.moduleName || '';

      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;

      const tabA = this.tabMetadataCache.get(a)?.tabName || '';
      const tabB = this.tabMetadataCache.get(b)?.tabName || '';
      return tabA < tabB ? -1 : tabA > tabB ? 1 : 0;
    });

    this.excludedTabs = (this.excludedTabs ?? []).filter((tabKey) => this.editableTabs.includes(tabKey));
  }

  private traverseTabs(
    source: any,
    callback: (modKey: string, secKey: string, tabKey: string, tab: ApplicationTabRatePlanFindInterface) => void
  ) {
    for (const [modKey, module] of Object.entries(source)) {
      for (const [secKey, section] of Object.entries((module as any).sections)) {
        for (const [tabKey, tab] of Object.entries((section as any).tabs)) {
          callback(modKey, secKey, tabKey, tab as ApplicationTabRatePlanFindInterface);
        }
      }
    }
  }

  private initMetadataCache() {
    this.traverseTabs(APPLICATION_ACCESS_CONTROL_RATE_PLAN_REFERENCE, (modKey, _, tabKey, tab) => {
      this.tabMetadataCache.set(tabKey, {
        tabName: tab.displayName,
        moduleName: APPLICATION_MODULES[modKey]?.displayName || modKey
      });
    });
  }
}
